import React, { useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import { OverFlowIcon } from 'assests/GmsIcons';
import { Edit, Delete } from 'Components/DesignSystem/Iconography/Icons';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';
import { useNavigate } from "react-router";
import { deleteIterationMethod, fetchIterationTableData } from '../../services/IterationServices';
import usePagination from 'Components/Common/hooks/usePagination';
import CSTablePagination from '../CSTablePagination';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { ConfirmationDialog } from 'Pages/GMS/ISR/shared/components/Common/ConfirmationDialog';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { TableColumn, SortDirection } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';

type DeleteConfirmation = {
    iteration: IterationTableItem | null;
    isOpen: boolean;
};

/**
 * The Iteration Method list, on the design-system `Table` — the § 141 recipe
 * (`ABTable`): columns with `render`, `striped hoverable bordered`, the kebab as a
 * portalled DS `Menu` with `onDismiss`, sort fed back through `sortState`.
 *
 * Every behavioural gate is unchanged: the fetch effect and its
 * `[sortBy, sortOrder, paginationParams, channelKey]` deps, the permission toasts in
 * `handleEdit`/`handleDelete`, the delete confirm/refetch path, and the three early
 * returns (skeleton / permission-denied / no-data).
 *
 * Sort mapping: the API always wants a direction (1 | -1), so the DS cycle's third
 * state (null) folds back to 'asc' — same-column clicks toggle exactly as before.
 *
 * What the hand-rolled table did that this stops doing: `tw-w-[${20}%]` template
 * literals Tailwind's scanner never saw (the column widths were silently dead
 * classes), a mousedown + capture-scroll listener pair the DS Menu's `onDismiss`
 * replaces (scroll-close kept — the page scrolls under a `tw-fixed` menu), and
 * per-row `role="menu"` markup on `<Link to="#">`s.
 */
const IterationTable = () => {
    const [sortBy, setSortBy] = useState<IterationSortOption>('category_name');
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [noPermission, setNoPermission] = useState<boolean>(false);
    const [iterationMethods, setIterationMethods] = useState<IterationTableItem[] | undefined>(undefined);
    const [showIndex, setShowIndex] = useState<number | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
        iteration: null,
        isOpen: false
    });
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const navigate = useNavigate();
    const { updatePermission, deletePermission, industryId, channelKey } = useContext<IterationContextType>(IterationContext);
    const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination();

    /* The menu is `tw-fixed` and the PAGE scrolls (no internal scroll region here), so
       a scroll would leave it floating over the wrong row — close it, as the legacy
       listener did. Outside-click and Escape are the DS Menu's `onDismiss`. */
    useEffect(() => {
        if (showIndex === null) return;
        const handleScroll = () => setShowIndex(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [showIndex]);

    useEffect(() => {
        fetchData();
    }, [sortBy, sortOrder, paginationParams, channelKey]);

    const fetchData = async () => {
        try {
            setLoading(true);
            if (!industryId || !channelKey) return
            const response = await fetchIterationTableData(
                industryId,
                channelKey,
                paginationParams.page,
                paginationParams.per_page,
                sortBy,
                sortOrder
            );
            if (response?.message === "Permission denied!!") {
                setNoPermission(true);
            } else {
                setNoPermission(false);
            }

            setIterationMethods(response?.data ?? undefined);
            updateTotalRecords(response?.pagination?.total ?? 0);
        } catch (e) {
            toast.error(e as string);
        }
        finally {
            setLoading(false);
        }
    }

    const handleSortChange = (columnId: string, direction: SortDirection) => {
        // The API always carries a direction: fold the DS cycle's null back to 'asc',
        // which keeps same-column clicks toggling asc <-> desc exactly as before.
        const next = direction ?? 'asc';
        setSortBy(columnId as IterationSortOption);
        setSortOrder(next === 'desc' ? -1 : 1);
    }

    const handleEdit = (iteration: IterationTableItem) => {
        if (!updatePermission) { toast.error("You don't have permission to edit"); return; }
        navigate(`/gms/substitutions_iteration/${iteration?.id}/edit`);
    }

    const handleDelete = (iteration: IterationTableItem) => {
        if (!deletePermission) {
            toast.error("You don't have permission to delete");
            return;
        }

        setDeleteConfirmation({
            iteration,
            isOpen: true
        });
    }

    const confirmDelete = async () => {
        if (!deleteConfirmation.iteration) return;

        try {
            setIsDeleting(true);
            const res = await deleteIterationMethod(deleteConfirmation.iteration.id);
            if (res?.done) {
                toast.success("Computed substitution deleted successfully");
                fetchData();
            } else {
                toast.warning(res?.message || "Delete operation failed");
            }
        } catch (error) {
            toast.error("Failed to delete computed substitution");
            console.error("Delete error:", error);
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                iteration: null,
                isOpen: false
            });
        }
    }

    const cancelDelete = () => {
        setDeleteConfirmation({
            iteration: null,
            isOpen: false
        });
    }

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setShowIndex(prev => (prev === index ? null : index));
    };

    const renderRowActions = (iteration: IterationTableItem, rowIndex: number) => {
        const menuItems: MenuItem[] = [
            {
                id: 'edit', label: 'Edit', icon: <Edit />,
                disabled: !updatePermission,
                'data-testid': `overflow-edit-${iteration.id}`,
                onClick: () => { setShowIndex(null); handleEdit(iteration); },
            },
            {
                id: 'delete', label: 'Delete', icon: <Delete />,
                disabled: !deletePermission,
                'data-testid': `overflow-delete-${iteration.id}`,
                onClick: () => { setShowIndex(null); handleDelete(iteration); },
            },
        ];

        return (
            <>
                <button
                    type="button"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={showIndex === rowIndex}
                    data-testid={`iteration-row-overflow-${iteration.id}`}
                    className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                    /* The Menu dismisses on document MOUSEDOWN (useDismissable § 2); without
                       this, pressing the open trigger dismisses-then-reopens on the same
                       click. Stopping the mousedown leaves the click toggle the only voice. */
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleOverflowClick(e, rowIndex)}
                >
                    <OverFlowIcon />
                </button>
                {/* Portalled to body: the DS Table wrapper is an overflow container, so
                    an absolutely positioned popover inside it would be clipped. */}
                {showIndex === rowIndex ? ReactDOM.createPortal(
                    <div
                        className="tw-fixed tw-z-[9999] tw-w-40"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`iteration-row-overflow-menu-${iteration.id}`}
                            onDismiss={() => setShowIndex(null)}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<IterationTableItem>[] = [
        {
            id: 'category_name',
            header: 'Category',
            accessor: (r) => r?.category_name ?? '',
            width: '30%',
            sortable: true,
            render: (_value, row) => (
                <span className="tw-block tw-truncate" title={row?.category_name}>{row?.category_name}</span>
            ),
        },
        {
            id: 'iterations',
            header: 'Iterations',
            accessor: (r) => r?.iterations,
            width: '20%',
            sortable: true,
        },
        {
            id: 'created_by',
            header: 'Created by',
            accessor: (r) => r?.created_by ?? '',
            width: '25%',
            sortable: true,
            render: (_value, row) => (
                <span className="tw-block tw-truncate" title={row?.created_by}>{row?.created_by}</span>
            ),
        },
        {
            id: 'updated_at',
            header: 'Last updated on',
            accessor: (r) => formatDateDDMMMYYYY(r?.updated_at),
            width: '20%',
            sortable: true,
            cellType: 'meta',
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '48px',
            align: 'right',
            render: (_value, row, rowIndex) => renderRowActions(row, rowIndex),
        },
    ];

    if (loading) { return <div className=""><TableSkeletonElement /></div> }
    if (noPermission && !loading && !iterationMethods?.length) return <NoDataComponent title='Permission Denied!' message="Looks like you don't have enough permission to access this feature or resource." />
    if (!loading && !iterationMethods?.length) return <NoDataComponent />

    return (
        <>
            <div className="tw-mt-3 tw-mb-4">
                <Table
                    /* Bound the grid so <main> stops scrolling the page — see
                       `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                       needs it: sticky resolves against a scrolling ancestor. */
                    stickyHeader
                    maxHeight={PAGE_GRID_MAX_HEIGHT}
                    columns={columns}
                    data={iterationMethods ?? []}
                    rowKey="id"
                    striped
                    hoverable
                    bordered
                    sortState={{ columnId: sortBy, direction: sortOrder === 1 ? 'asc' : 'desc' }}
                    onSort={handleSortChange}
                    id="iteration-table"
                    data-testid="iteration-table"
                />
            </div>

            {!loading && iterationMethods && iterationMethods.length > 0 && (
                <CSTablePagination
                    paginationParams={paginationParams}
                    totalRecords={totalRecords}
                    updatePageSize={updatePageSize}
                    handlePageChange={handlePageChange}
                />
            )}

            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Computed Substitution"
                message="Are you sure you want to delete this computed substitution?"
                secondaryMessage='This action cannot be undone.'
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                isLoading={isDeleting}
            />
        </>
    );
};

export default IterationTable;
